import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Drawer } from "@/components/layout/Drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Building2 } from "lucide-react";
import { Field, DrawerSection } from "@/components/forms/fields";
import { useDuplicateCheck } from "@/hooks/useDuplicateCheck";
import { clientsService } from "@/services/resourceServices";
import { formatApiErrorDetail } from "@/services/api";
import { isOwnerRole } from "@/lib/permissions/permissions";
import { useAuth } from "@/hooks/useAuth";

const EMPTY = {
  client_name: "",
  company_name: "",
  email: "",
  phone: "",
  notes: "",
  login_enabled: false,
  status: "active",
  assigned_va_agency_id: "none",
  warehouse_id: "",
};

export function ClientDrawer({ open, onOpenChange, record, agencies = [], warehouses = [], onSaved }) {
  const { user: currentUser } = useAuth();
  const isOwner = isOwnerRole(currentUser?.role);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!record;

  useEffect(() => {
    if (open) {
      setForm(
        record
          ? {
              client_name: record.client_name || "",
              company_name: record.company_name || "",
              email: record.email || "",
              phone: record.phone || "",
              notes: record.notes || "",
              login_enabled: !!record.login_enabled,
              status: record.status || "active",
              assigned_va_agency_id: record.assigned_va_agency_id || "none",
              warehouse_id: record.warehouse_id || "",
            }
          : EMPTY
      );
    }
  }, [open, record]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const { result: dup } = useDuplicateCheck(
    () =>
      clientsService.checkDuplicate({
        email: form.email,
        name: form.client_name,
        exclude_id: record?.id || "",
      }),
    [form.email, form.client_name, open],
    open && (!!form.email.trim() || !!form.client_name.trim())
  );

  const emailConflict = form.login_enabled && dup?.email_conflict;
  const nameWarning = dup?.name_warning;

  const nameError = !form.client_name.trim() ? "Client name is required" : "";
  const warehouseError = isOwner && !isEdit && !form.warehouse_id ? "Select a warehouse" : "";
  const emailError =
    form.login_enabled && !form.email.trim()
      ? "Email is required to enable login"
      : emailConflict
      ? "This email is already linked to a login account"
      : "";
  const canSave = !nameError && !emailError && !warehouseError && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const body = {
        client_name: form.client_name.trim(),
        company_name: form.company_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        notes: form.notes.trim(),
        login_enabled: form.login_enabled,
        status: form.status,
        assigned_va_agency_id:
          form.assigned_va_agency_id === "none" ? null : form.assigned_va_agency_id,
        ...(isOwner && !isEdit ? { warehouse_id: form.warehouse_id } : {}),
      };
      const res = isEdit
        ? await clientsService.update(record.id, body)
        : await clientsService.create(body);
      toast.success(isEdit ? "Client updated" : "Client created");
      onOpenChange(false);
      onSaved?.(res);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      icon={Building2}
      title={isEdit ? "Edit Client" : "New Client"}
      description={isEdit ? "Update client details." : "Add a customer of your 3PL."}
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="client-drawer-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave} data-testid="client-drawer-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Save changes" : "Create client"}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <DrawerSection title="Client details">
          <Field label="Client Name" htmlFor="client_name" required error={nameError} warning={!nameError ? (nameWarning ? "A client with this name already exists." : "") : ""}>
            <Input
              id="client_name"
              value={form.client_name}
              onChange={(e) => set("client_name")(e.target.value)}
              placeholder="Northwind Co."
              data-testid="client-name-input"
            />
          </Field>
          <Field label="Company Name" htmlFor="company_name">
            <Input
              id="company_name"
              value={form.company_name}
              onChange={(e) => set("company_name")(e.target.value)}
              placeholder="Northwind Traders LLC"
              data-testid="client-company-input"
            />
          </Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Email" htmlFor="email" required={form.login_enabled} error={emailError}>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => set("email")(e.target.value)}
                placeholder="ops@northwind.com"
                data-testid="client-email-input"
              />
            </Field>
            <Field label="Phone" htmlFor="phone">
              <Input
                id="phone"
                value={form.phone}
                onChange={(e) => set("phone")(e.target.value)}
                placeholder="+1 (555) 000-0000"
                data-testid="client-phone-input"
              />
            </Field>
          </div>
        </DrawerSection>

        <DrawerSection title="Assignment & status">
          {isOwner && !isEdit && (
            <Field label="Warehouse" required error={warehouseError}
                   hint="Which warehouse organization does this client belong to?">
              <Select value={form.warehouse_id || undefined} onValueChange={set("warehouse_id")}>
                <SelectTrigger data-testid="client-warehouse-select">
                  <SelectValue placeholder="Select a warehouse" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Status">
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="client-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Assigned VA Agency">
              <Select value={form.assigned_va_agency_id} onValueChange={set("assigned_va_agency_id")}>
                <SelectTrigger data-testid="client-agency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {agencies.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
        </DrawerSection>

        <DrawerSection title="Login access">
          <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-foreground">Enable client login</p>
              <p className="text-xs text-muted-foreground">
                Sends a secure invitation. The client sets their own password.
              </p>
            </div>
            <Switch
              checked={form.login_enabled}
              onCheckedChange={set("login_enabled")}
              data-testid="client-login-switch"
            />
          </div>
        </DrawerSection>

        <DrawerSection title="Notes">
          <Field label="Notes" htmlFor="notes">
            <Textarea
              id="notes"
              value={form.notes}
              onChange={(e) => set("notes")(e.target.value)}
              placeholder="Internal notes about this client…"
              rows={3}
              data-testid="client-notes-input"
            />
          </Field>
        </DrawerSection>
      </div>
    </Drawer>
  );
}
